// kontaktstatusWahl.ts — DOM-freie Logik des Popups „Kontaktstatus wählen"
// (User 28.09.2026). EIN Modell für zwei Stellen:
//   - Status-Pille in der Akte: Hauptstatus + Rollen ÄNDERN,
//   - Spaltenfilter „K-Status" der Kontaktliste: danach FILTERN.
//
// Ein Kontakt hat genau EINEN Hauptstatus (Kunde | Interessent | Sonstige) und
// beliebig viele Rollen aus „Sonstige" (Katalog: Einstellungen →
// Kontaktrollen, `data/kontaktRollen.ts`). Gespeichert wird wie in der
// Kontaktanlage (`statusFuerApi`): Primär-Status = Kunde/Interessent bzw.
// unter „Sonstige" die erste Rolle, alle übrigen Rollen als Zusatz-Status
// (`ContactStatusLink`).

import type { RollenBereich } from "../../data/kontaktRollen";
import { rolleZu } from "../../data/kontaktRollen";
import { STATUS_MFU, STATUS_MFU_ALT, kanonischerStatus } from "../../data/sonstigeStatusKatalog";
import {
  ROLLEN_GRUPPEN_FILTER,
  SONSTIGE_GRUPPE_WERT,
  kStatusBereichAusWert,
  kontaktstatusFilterPayload,
  rollenWerteDesBereichs,
} from "./table/sonstigeFilterModel";

export type HauptStatus = "Kunde" | "Interessent" | "Sonstige";
export const HAUPT_STATI: HauptStatus[] = ["Kunde", "Interessent", "Sonstige"];

export type KontaktstatusWahl = {
  /** `null` = keiner (Filter: egal; Akte: „ohne Status"). */
  haupt: HauptStatus | null;
  /** Rollen-Werte (Speicherwerte), Reihenfolge = Auswahlreihenfolge. */
  rollen: string[];
};

// Alte Namen („Mensch fürs Unternehmen") gleich auf den heutigen abbilden —
// sonst stünde eine Rolle aus einem alten Cache doppelt bzw. ohne Häkchen da.
const eindeutig = (xs: string[]): string[] => Array.from(new Set(xs.map(kanonischerStatus).filter(Boolean)));

const istHaupt = (s: string): s is HauptStatus => (HAUPT_STATI as string[]).includes(s);

// ── Akte: gespeicherter Stand ↔ Auswahl ──────────────────────────────────

/**
 * Primär-Status + Zusatz-Status → Auswahl. Alles, was nicht Kunde/Interessent
 * ist, ist eine Rolle unter „Sonstige" — auch „Altkunde", „Haushaltsmitglied"
 * oder Altbestand, den der Katalog nicht mehr kennt (sonst ginge er beim
 * Speichern verloren). „Unbekannt" gilt als „ohne Status".
 */
export function wahlAusKontakt(status?: string | null, extraStatuses?: string[] | null): KontaktstatusWahl {
  const primaer = kanonischerStatus(status ?? "");
  const extras = eindeutig(extraStatuses ?? []).filter((s) => !istHaupt(s) && s !== "Unbekannt");
  if (primaer === "Kunde" || primaer === "Interessent") return { haupt: primaer, rollen: extras };
  if (!primaer || primaer === "Unbekannt") {
    return { haupt: extras.length ? "Sonstige" : null, rollen: extras };
  }
  return {
    haupt: "Sonstige",
    rollen: eindeutig([...(primaer === "Sonstige" ? [] : [primaer]), ...extras]),
  };
}

/**
 * Auswahl → Body von `PUT /contact/:id/kontaktstatus` (Speicherform der
 * Anlage). `null`, solange kein Hauptstatus gewählt ist.
 */
export function wahlFuerApi(w: KontaktstatusWahl): { statusName: string; weitereStatus: string[] } | null {
  const rollen = eindeutig(w.rollen);
  if (w.haupt === "Kunde" || w.haupt === "Interessent") return { statusName: w.haupt, weitereStatus: rollen };
  if (w.haupt === "Sonstige") {
    const [erste, ...rest] = rollen;
    return { statusName: erste ?? "Sonstige", weitereStatus: rest };
  }
  return null;
}

export const gleicheWahl = (a: KontaktstatusWahl, b: KontaktstatusWahl): boolean =>
  a.haupt === b.haupt &&
  a.rollen.length === b.rollen.length &&
  a.rollen.every((r) => b.rollen.includes(r));

// ── Pille ────────────────────────────────────────────────────────────────

/** Der Speicherwert von „Verantwortung fürs Unternehmen" (plus Altname bis 29.09.2026). */
export const STATUS_VFU = STATUS_MFU;
const VFU_WERTE = [STATUS_VFU, STATUS_MFU_ALT];
/** Rollen, die die Pille NIE zeigt — auch nicht im „+N". */
export const NIE_IN_DER_PILLE = ["Mensch im Unternehmen", "Haushaltsmitglied"];

/**
 * Was die Pille zeigt: Hauptstatus, von den Rollen NUR „Verantwortung fürs
 * Unternehmen" ausgeschrieben, jede weitere (nicht ausgeblendete) Rolle nur
 * als Zahl. „Mensch im Unternehmen" und „Haushaltsmitglied" zählen nicht.
 */
export function pillenAnzeige(
  status?: string | null,
  extraStatuses?: string[] | null,
): { haupt: HauptStatus | null; vfu: boolean; mehr: number; rollen: string[] } {
  const w = wahlAusKontakt(status, extraStatuses);
  const sichtbar = w.rollen.filter((r) => !NIE_IN_DER_PILLE.includes(r));
  const vfu = sichtbar.some((r) => VFU_WERTE.includes(r));
  const mehr = sichtbar.filter((r) => !VFU_WERTE.includes(r)).length;
  return { haupt: w.haupt, vfu, mehr, rollen: w.rollen };
}

/** Beschriftung einer Rolle aus dem aktiven Katalog (Altbestand: der Wert). */
export const rollenLabel = (wert: string): string => rolleZu(wert)?.label ?? wert;

// ── Suche ────────────────────────────────────────────────────────────────

/**
 * Umlaut-tolerante Normalform: „fur", „für" und „fuer" treffen dasselbe.
 * Beide Seiten gehen durch dieselbe Faltung, deshalb darf sie grob sein.
 */
export function suchNorm(s: string): string {
  return s
    .toLowerCase()
    .replace(/ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/ü/g, "u")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ae/g, "a")
    .replace(/oe/g, "o")
    .replace(/ue/g, "u")
    .replace(/\s+/g, " ")
    .trim();
}

/** Trifft `text` die Suche? Jedes Wort der Suche muss vorkommen. */
export function trifft(text: string, suche: string): boolean {
  const q = suchNorm(suche);
  if (!q) return true;
  const t = suchNorm(text);
  return q.split(" ").every((w) => t.includes(w));
}

/**
 * Katalog nach der Suche: ein Bereich, dessen Titel passt, bleibt komplett;
 * sonst nur die passenden Rollen (Label ODER Speicherwert). Leere Bereiche
 * fallen weg.
 */
export function gefilterteBereiche(bereiche: RollenBereich[], suche: string): RollenBereich[] {
  if (!suchNorm(suche)) return bereiche;
  return bereiche
    .map((b) =>
      trifft(b.titel, suche)
        ? b
        : { ...b, rollen: b.rollen.filter((r) => trifft(r.label, suche) || trifft(r.value, suche)) },
    )
    .filter((b) => b.rollen.length > 0);
}

// ── Mehrfachauswahl ──────────────────────────────────────────────────────
// Generisch über `rollen`: dieselben Helfer tragen die Akte (ein Hauptstatus)
// und den Filter (mehrere Hauptstatus, `KStatusFilterWahl`).

type MitRollen = { rollen: string[] };

export function rolleUmschalten<T extends MitRollen>(w: T, rolle: string): T {
  return w.rollen.includes(rolle)
    ? { ...w, rollen: w.rollen.filter((r) => r !== rolle) }
    : { ...w, rollen: [...w.rollen, rolle] };
}

/** Zustand eines Bereichs-Häkchens. */
export function bereichZustand(w: MitRollen, werte: string[]): "alle" | "teils" | "keine" {
  const n = werte.filter((v) => w.rollen.includes(v)).length;
  return n === 0 ? "keine" : n === werte.length ? "alle" : "teils";
}

/** Bereich komplett an (wenn nicht alle an) bzw. komplett aus. */
export function bereichUmschalten<T extends MitRollen>(w: T, werte: string[]): T {
  if (bereichZustand(w, werte) === "alle") return { ...w, rollen: w.rollen.filter((r) => !werte.includes(r)) };
  return { ...w, rollen: eindeutig([...w.rollen, ...werte]) };
}

// ── Spaltenfilter „K-Status" ─────────────────────────────────────────────
//
// Seit 02.10.2026 lassen sich im Filter MEHRERE Hauptstatus wählen (User:
// „Kunde und Interessent", „auch Sonstige + andere"). Der Filterwert ist JSON:
//   {"haupt":["Kunde","Interessent"],"rollen":["Vermittler, Tippgeber"]}
// Semantik: ODER zwischen den Hauptstatus (Backend `KontaktstatusHaupt`),
// UND zwischen Hauptstatus und Rollen, ODER innerhalb der Rollen
// (`KontaktstatusRollen`: Primär- oder Zusatz-Status).
//
// Weiter lesbar (gespeicherte Tabellenstände, sessionStorage):
//   - JSON vom 28.09.2026 mit EINEM Hauptstatus: {"haupt":"Interessent",…},
//   - reine Werte: Status, `__sonstige__`, `__bereich__:…`, Rollen-Sammelzeilen.

/** Auswahl im Spaltenfilter: beliebig viele Hauptstatus plus Rollen. */
export type KStatusFilterWahl = {
  /** Leer = Hauptstatus egal. Reihenfolge immer wie `HAUPT_STATI`. */
  haupt: HauptStatus[];
  rollen: string[];
};

export const LEERE_FILTER_WAHL: KStatusFilterWahl = { haupt: [], rollen: [] };

const hauptGeordnet = (xs: HauptStatus[]): HauptStatus[] => HAUPT_STATI.filter((h) => xs.includes(h));

/** Einen Hauptstatus im Filter an-/abwählen. */
export function hauptUmschalten(w: KStatusFilterWahl, h: HauptStatus): KStatusFilterWahl {
  return {
    ...w,
    haupt: w.haupt.includes(h) ? w.haupt.filter((x) => x !== h) : hauptGeordnet([...w.haupt, h]),
  };
}

export const gleicheFilterWahl = (a: KStatusFilterWahl, b: KStatusFilterWahl): boolean =>
  a.haupt.length === b.haupt.length &&
  a.haupt.every((h) => b.haupt.includes(h)) &&
  a.rollen.length === b.rollen.length &&
  a.rollen.every((r) => b.rollen.includes(r));

export function kStatusFilterWert(w: KStatusFilterWahl): string | undefined {
  const haupt = hauptGeordnet(w.haupt);
  const rollen = eindeutig(w.rollen);
  if (haupt.length === 0 && rollen.length === 0) return undefined;
  return JSON.stringify({ haupt, rollen });
}

/**
 * Der Standardfilter der Kontaktliste (User 01.10.2026): Kunde + Interessent,
 * auch mit Rollen — „Sonstige" sind zunächst ausgeblendet.
 */
export const STANDARD_K_STATUS_WAHL: KStatusFilterWahl = { haupt: ["Kunde", "Interessent"], rollen: [] };
export const STANDARD_K_STATUS_WERT: string = kStatusFilterWert(STANDARD_K_STATUS_WAHL)!;

function ausJson(wert: string): KStatusFilterWahl | null {
  if (!wert.startsWith("{")) return null;
  try {
    const o = JSON.parse(wert) as { haupt?: unknown; rollen?: unknown };
    if (!o || typeof o !== "object") return null;
    // Neu: Liste. Alt (28.09.2026): ein einzelner String oder null.
    const rohHaupt = Array.isArray(o.haupt) ? o.haupt : typeof o.haupt === "string" ? [o.haupt] : [];
    const haupt = hauptGeordnet(rohHaupt.filter((h): h is HauptStatus => typeof h === "string" && istHaupt(h)));
    const rollen = Array.isArray(o.rollen) ? eindeutig(o.rollen.filter((r): r is string => typeof r === "string")) : [];
    return { haupt, rollen };
  } catch {
    return null;
  }
}

/** Filterwert (neu oder alt) → Auswahl im Popup. */
export function kStatusWahlAusFilterWert(wert: string | undefined): KStatusFilterWahl {
  if (!wert) return { haupt: [], rollen: [] };
  const neu = ausJson(wert);
  if (neu) return neu;
  if (wert === "Kunde" || wert === "Interessent") return { haupt: [wert], rollen: [] };
  if (wert === SONSTIGE_GRUPPE_WERT || wert === "Sonstige") return { haupt: ["Sonstige"], rollen: [] };
  const bereich = kStatusBereichAusWert(wert);
  if (bereich !== null) return { haupt: ["Sonstige"], rollen: rollenWerteDesBereichs(bereich) };
  const gruppe = ROLLEN_GRUPPEN_FILTER.find((g) => g.wert === wert);
  if (gruppe) return { haupt: [], rollen: gruppe.status };
  return { haupt: [], rollen: [wert] };
}

/** Backend-Schlüssel der Hauptstatus-Mehrfachauswahl (siehe `contactStatusHauptFilter.ts`). */
export const K_STATUS_HAUPT_KEY = "KontaktstatusHaupt";

/** Anteil des K-Status-Filters am `filters`-Objekt der Kontaktliste. */
export function kStatusFilterPayload(wert: string | boolean | undefined): Record<string, unknown> {
  if (typeof wert !== "string" || wert === "") return {};
  const w = ausJson(wert);
  // Alter reiner Wert aus einem gespeicherten Tabellenstand: unverändert wie bisher.
  if (!w) return kontaktstatusFilterPayload(wert);
  const out: Record<string, unknown> = {};
  // Alle drei gewählt heißt „egal" — dann schränkt der Hauptstatus nichts ein.
  if (w.haupt.length > 0 && w.haupt.length < HAUPT_STATI.length) out[K_STATUS_HAUPT_KEY] = w.haupt;
  if (w.rollen.length) out.KontaktstatusRollen = w.rollen;
  return out;
}

/** Kurzbeschreibung einer Auswahl, z. B. „Kunde, Interessent · Vermittler/Tippgeber +1". */
export function wahlBeschreibung(w: KStatusFilterWahl): string {
  const teile: string[] = [];
  if (w.haupt.length) teile.push(hauptGeordnet(w.haupt).join(", "));
  if (w.rollen.length) {
    teile.push(rollenLabel(w.rollen[0]) + (w.rollen.length > 1 ? ` +${w.rollen.length - 1}` : ""));
  }
  return teile.join(" · ");
}
